import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, FC, KeyboardEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DeleteOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'

import { Input, Skeleton } from '@/components'
import { rqKeys } from '@/react-query-keys'
import { isCordovaApp } from '@/helpers'
import { useAppStore } from '@/store'
import { apiUploadRequest } from '@/helpers/request'
import {
  TICKET_ATTACHMENTS_ACCEPT,
  TICKET_ATTACHMENTS_MAX_FILES,
  TICKET_ATTACHMENTS_MAX_SIZE_BYTES,
  createAttachmentLocalId,
  downloadTicketFile,
  extractUploadedTicketFile,
  formatAttachmentSize,
  isTicketAttachmentAllowed,
  resolveTicketFileUrl,
  type T_TicketAttachmentDraft,
} from '@/pages/help/helpers/ticket-files'
import {
  fetchVisitorMessageList,
  markVisitorMessagesAsRead,
  sendVisitorMessage,
  useMessagesStore,
} from '../index'
import type { T_VisitorMessage } from '../types'
import {
  SC_AttachmentsHint,
  SC_AttachmentsList,
  SC_AttachButton,
  SC_AttachmentInfo,
  SC_AttachmentItem,
  SC_AttachmentMeta,
  SC_AttachmentName,
  SC_AttachmentRemoveButton,
  SC_Composer,
  SC_ComposerInputWrapper,
  SC_ComposerRow,
  SC_EmptyMessages,
  SC_MessageAuthor,
  SC_MessageBubble,
  SC_MessageDateSeparator,
  SC_MessageFileLink,
  SC_MessageFiles,
  SC_MessageRow,
  SC_MessageText,
  SC_MessageTime,
  SC_MessagesBody,
  SC_SendButton,
  SC_SystemMessageNote,
} from './styled'

type T_MessagesTopicViewProps = {
  topicId: number | null
  topicSubject?: string | null
  selectedMessageId?: number | null
  isReadonly?: boolean
}

const toSafeDate = (value: unknown): Date => {
  if (value instanceof Date) {
    const ts = value.getTime()
    return Number.isFinite(ts) ? value : new Date()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const timestamp = value > 0 && value < 1e12 ? value * 1000 : value
    return new Date(timestamp)
  }

  if (typeof value === 'string' && value.trim()) {
    const asNumber = Number(value)
    if (Number.isFinite(asNumber)) {
      const timestamp = asNumber > 0 && asNumber < 1e12 ? asNumber * 1000 : asNumber
      return new Date(timestamp)
    }

    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) {
      return new Date(parsed)
    }
  }

  return new Date()
}

const getDayKey = (value: unknown) => {
  const date = toSafeDate(value)
  return (
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
  )
}

const messageDayFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const getDayLabel = (value: unknown) => {
  const date = toSafeDate(value)
  return messageDayFormatter.format(date).toUpperCase()
}

const getTimeLabel = (value: unknown) => {
  const date = toSafeDate(value)
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${hh}:${mm}`
}

export const MessagesTopicView: FC<T_MessagesTopicViewProps> = ({
  topicId,
  topicSubject,
  selectedMessageId,
  isReadonly = false,
}) => {
  const queryClient = useQueryClient()
  const userId = useAppStore((s) => s.userData.id)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const messageRefs = useRef<Map<number, HTMLDivElement>>(new Map())

  const unreadMessages = useMessagesStore((s) => s.unreadMessages)
  const markMessagesAsRead = useMessagesStore((s) => s.markMessagesAsRead)

  const [ draftMessage, setDraftMessage ] = useState('')
  const [ files, setFiles ] = useState<T_TicketAttachmentDraft[]>([])
  const [ isUploadPending, setIsUploadPending ] = useState(false)
  const [ previousMessagesLength, setPreviousMessagesLength ] = useState(0)

  const {
    data: topicPayload,
    isPending: isTopicMessagesPending,
  } = useQuery({
    queryKey: [ rqKeys.visitorMessageTopicList, topicId ],
    queryFn: async () => fetchVisitorMessageList(topicId as number),
    enabled: Number.isFinite(topicId) && Number(topicId) > 0,
  })

  const messages = useMemo<T_VisitorMessage[]>(() => {
    const topicMessages = (topicPayload?.messages || []).map((message) => ({
      ...message,
      timestamp: toSafeDate(message.timestamp),
    }))
    if (!topicId) return topicMessages

    const map = new Map<number, T_VisitorMessage>()
    topicMessages.forEach((message) => {
      map.set(message.id, message)
    })

    unreadMessages
      .filter((message) => Number(message.topic?.id) === Number(topicId))
      .forEach((message) => {
        map.set(message.id, {
          ...message,
          timestamp: toSafeDate(message.timestamp),
        })
      })

    return Array.from(map.values())
      .sort((a, b) => toSafeDate(a.timestamp).getTime() - toSafeDate(b.timestamp).getTime())
  }, [ topicPayload?.messages, unreadMessages, topicId ])

  const unreadIdsInTopic = useMemo(() => {
    const normalizeSubject = (value: string) => value.trim().toLowerCase()
    const normalizedTopicId = Number(topicId)
    const hasValidTopicId = Number.isFinite(normalizedTopicId) && normalizedTopicId > 0
    const effectiveTopicSubject = normalizeSubject(
      topicSubject
      || topicPayload?.topic?.subject
      || ''
    )

    if (!hasValidTopicId && !effectiveTopicSubject && !selectedMessageId) {
      return [] as number[]
    }

    const ids = new Set<number>()

    unreadMessages
      .filter((message) => {
        const messageTopicId = Number(message.topic?.id)
        if (hasValidTopicId && Number.isFinite(messageTopicId) && messageTopicId > 0) {
          return messageTopicId === normalizedTopicId
        }

        if (!effectiveTopicSubject) return false
        const messageTopicSubject = normalizeSubject(message.topic?.subject || '')
        return !!messageTopicSubject && messageTopicSubject === effectiveTopicSubject
      })
      .forEach((message) => {
        const id = Number(message.id)
        if (Number.isFinite(id) && id > 0) {
          ids.add(id)
        }
      })

    const selectedId = Number(selectedMessageId)
    if (Number.isFinite(selectedId) && selectedId > 0) {
      const isSelectedUnread = unreadMessages.some((message) => Number(message.id) === selectedId)
      if (isSelectedUnread) {
        ids.add(selectedId)
      }
    }

    return Array.from(ids)
  }, [ unreadMessages, topicId, topicSubject, topicPayload?.topic?.subject, selectedMessageId ])

  useEffect(() => {
    if (!unreadIdsInTopic.length) return

    markMessagesAsRead(unreadIdsInTopic)
    void markVisitorMessagesAsRead(unreadIdsInTopic).catch((error) => {
      console.warn('[messages] failed to mark messages as read', error)
      void queryClient.invalidateQueries({ queryKey: [ rqKeys.visitorMessageUnread ], exact: false })
    })
  }, [ unreadIdsInTopic, markMessagesAsRead, queryClient ])

  useEffect(() => {
    if (!messages.length) {
      setPreviousMessagesLength(0)
      return
    }

    if (messages.length > previousMessagesLength) {
      bodyRef.current?.scrollTo({
        top: bodyRef.current.scrollHeight,
        behavior: previousMessagesLength === 0 ? 'auto' : 'smooth',
      })
      setPreviousMessagesLength(messages.length)
    }
  }, [ messages, previousMessagesLength ])

  useEffect(() => {
    if (!selectedMessageId) return

    const target = messageRefs.current.get(selectedMessageId)
    if (!target) return
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [ selectedMessageId, messages ])

  const uploadFiles = async (nextFiles: File[]) => {
    if (!nextFiles.length) return

    const freeSlots = TICKET_ATTACHMENTS_MAX_FILES - files.length
    if (freeSlots <= 0) {
      toast.error(`Можно прикрепить не более ${TICKET_ATTACHMENTS_MAX_FILES} файлов`)
      return
    }

    const filesToUpload = nextFiles.slice(0, freeSlots)
    if (nextFiles.length > freeSlots) {
      toast.error(`Можно прикрепить не более ${TICKET_ATTACHMENTS_MAX_FILES} файлов`)
    }

    const tooBigFiles = filesToUpload.filter((file) => file.size > TICKET_ATTACHMENTS_MAX_SIZE_BYTES)
    if (tooBigFiles.length) {
      toast.error('Один или несколько файлов превышают размер 5 MB')
    }

    const unsupportedFiles = filesToUpload.filter((file) => !isTicketAttachmentAllowed(file))
    if (unsupportedFiles.length) {
      toast.error('Загружаемый файл имеет неподдерживаемый формат.')
    }

    const validFiles = filesToUpload.filter(
      (file) => file.size <= TICKET_ATTACHMENTS_MAX_SIZE_BYTES && isTicketAttachmentAllowed(file)
    )
    if (!validFiles.length) return

    setIsUploadPending(true)
    const uploadedFiles: T_TicketAttachmentDraft[] = []

    for (const file of validFiles) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', 'message')

      try {
        const uploadRes = await apiUploadRequest<unknown>(formData)
        const uploaded = extractUploadedTicketFile(uploadRes)
        if (!uploaded) throw new Error('Upload response does not contain file id')

        uploadedFiles.push({
          ...uploaded,
          localId: createAttachmentLocalId(),
          name: uploaded.title || file.name,
          size: file.size,
        })
      } catch (error) {
        console.error(error)
        toast.error(`Не удалось загрузить файл "${file.name}"`)
      }
    }

    setFiles((prev) => [ ...prev, ...uploadedFiles ])
    setIsUploadPending(false)
  }

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    e.target.value = ''
    await uploadFiles(selectedFiles)
  }

  const {
    mutate: sendMessageMutation,
    isPending: isSendMessagePending,
  } = useMutation({
    mutationFn: async () => {
      if (!topicId) return null
      return sendVisitorMessage(topicId, draftMessage.trim(), files.map((file) => file.id))
    },
    onSuccess: (createdMessage) => {
      if (!createdMessage || !topicId) return

      queryClient.setQueryData(
        [ rqKeys.visitorMessageTopicList, topicId ],
        (old: { topic?: unknown; messages?: T_VisitorMessage[] } | undefined) => {
          const prev = old?.messages || []
          return {
            ...(old || {}),
            messages: [ ...prev, createdMessage ],
          }
        },
      )

      void queryClient.invalidateQueries({ queryKey: [ rqKeys.visitorMessageTopics ], exact: true })
      setDraftMessage('')
      setFiles([])
    },
    onError: () => {
      toast.error('Не удалось отправить сообщение')
    },
  })

  const sendMessage = () => {
    if (!topicId || !draftMessage.trim() || isReadonly) return
    if (isSendMessagePending || isUploadPending) return
    sendMessageMutation()
  }

  const handleInputKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter') return
    if (e.shiftKey) return
    e.preventDefault()
    sendMessage()
  }

  const handleAttachmentDownload = async (fileUrl: string, fileName?: string) => {
    const loadingToastId = isCordovaApp
      ? toast.loading('Файл скачивается...')
      : null

    try {
      const downloadResult = await downloadTicketFile(fileUrl, fileName)

      if (loadingToastId !== null) {
        toast.update(loadingToastId, {
          render: downloadResult === 'cordova-system-browser'
            ? 'Открыли системный браузер для скачивания'
            : 'Файл скачан',
          type: 'success',
          isLoading: false,
          autoClose: 1800,
          closeOnClick: true,
        })
      }
    } catch (error) {
      console.error(error)

      if (loadingToastId !== null) {
        toast.update(loadingToastId, {
          render: 'Не удалось скачать файл',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
        })
        return
      }

      toast.error('Не удалось скачать файл')
    }
  }

  if (!topicId) {
    return (
      <SC_EmptyMessages>
        Выберите тему, чтобы просмотреть переписку
      </SC_EmptyMessages>
    )
  }

  return (
    <>
      <SC_MessagesBody ref={bodyRef}>
        {isTopicMessagesPending ? (
          <Skeleton count={4} />
        ) : (
          messages.length ? (
            messages.map((message, index) => {
              const isMine = String(message.author.id) === String(userId)
              const prev = messages[index - 1]
              const currentDayKey = getDayKey(message.timestamp)
              const prevDayKey = prev ? getDayKey(prev.timestamp) : ''
              const showDate = currentDayKey !== prevDayKey

              return (
                <div key={message.id}>
                  {showDate && (
                    <SC_MessageDateSeparator>
                      {getDayLabel(message.timestamp)}
                    </SC_MessageDateSeparator>
                  )}

                  <SC_MessageRow
                    $isMine={isMine}
                    ref={(node) => {
                      if (!node) return
                      messageRefs.current.set(message.id, node)
                    }}
                  >
                    <SC_MessageBubble $isMine={isMine}>
                      {!isMine && (
                        <SC_MessageAuthor>
                          {message.author.fullname || message.author.login || 'Отправитель'}
                        </SC_MessageAuthor>
                      )}

                      <SC_MessageText>{message.content}</SC_MessageText>

                      {!!message.files.length && (
                        <SC_MessageFiles>
                          {message.files.map((file) => {
                            const fileUrl = resolveTicketFileUrl(file.path)
                            if (!fileUrl) return null

                            return (
                              <SC_MessageFileLink
                                key={`${message.id}_${file.id}_${file.title}`}
                                href={fileUrl}
                                download={file.title || undefined}
                                rel='noreferrer noopener'
                                onClick={(e) => {
                                  e.preventDefault()
                                  void handleAttachmentDownload(fileUrl, file.title)
                                }}
                                $isMine={isMine}
                              >
                                <PaperClipOutlined />
                                {file.title || 'Вложение'}
                              </SC_MessageFileLink>
                            )
                          })}
                        </SC_MessageFiles>
                      )}

                      <SC_MessageTime $isMine={isMine}>
                        {getTimeLabel(message.timestamp)}
                      </SC_MessageTime>
                    </SC_MessageBubble>
                  </SC_MessageRow>
                </div>
              )
            })
          ) : (
            <SC_EmptyMessages>
              В этой теме пока нет сообщений
            </SC_EmptyMessages>
          )
        )}
      </SC_MessagesBody>

      {topicPayload?.topic?.isSystem ? (
        <SC_Composer>
          <SC_SystemMessageNote>
            Системное сообщение
          </SC_SystemMessageNote>
        </SC_Composer>
      ) : (
        <SC_Composer>
          <SC_ComposerRow>
            <SC_AttachButton
              type='button'
              onClick={() => fileInputRef.current?.click()}
              disabled={isReadonly || isSendMessagePending || isUploadPending || files.length >= TICKET_ATTACHMENTS_MAX_FILES}
              title='Прикрепить файл'
            >
              <PaperClipOutlined />
            </SC_AttachButton>

            <SC_ComposerInputWrapper>
              <Input.TextArea
                value={draftMessage}
                onChange={(e) => setDraftMessage(e.target.value)}
                onKeyDown={handleInputKeyDown}
                rows={1}
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder='Написать сообщение'
                disabled={isReadonly || isSendMessagePending || isUploadPending}
              />
            </SC_ComposerInputWrapper>

            <SC_SendButton
              type='button'
              onClick={sendMessage}
              disabled={isReadonly || isSendMessagePending || isUploadPending || !draftMessage.trim()}
              title='Отправить'
            >
              <SendOutlined />
            </SC_SendButton>
          </SC_ComposerRow>

          <SC_AttachmentsHint>
            Максимум {TICKET_ATTACHMENTS_MAX_FILES} файлов, до 5 MB каждый
          </SC_AttachmentsHint>

          {isUploadPending && (
            <SC_AttachmentsHint>
              Загрузка файлов...
            </SC_AttachmentsHint>
          )}

          {!!files.length && (
            <SC_AttachmentsList>
              {files.map((file) => (
                <SC_AttachmentItem key={file.localId}>
                  <SC_AttachmentInfo>
                    <SC_AttachmentName>{file.name}</SC_AttachmentName>
                    <SC_AttachmentMeta>{formatAttachmentSize(file.size)}</SC_AttachmentMeta>
                  </SC_AttachmentInfo>

                  <SC_AttachmentRemoveButton
                    type='button'
                    onClick={() => setFiles((prev) => prev.filter((item) => item.localId !== file.localId))}
                  >
                    <DeleteOutlined />
                  </SC_AttachmentRemoveButton>
                </SC_AttachmentItem>
              ))}
            </SC_AttachmentsList>
          )}

          <input
            ref={fileInputRef}
            type='file'
            hidden
            multiple
            accept={TICKET_ATTACHMENTS_ACCEPT}
            onChange={handleFileChange}
          />
        </SC_Composer>
      )}
    </>
  )
}
